import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faMobileScreen, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { useAppSelector } from "../../store/hooks";
import {
  useGetPushStatusQuery,
  useSendTestPushMutation,
  useUnsubscribePushMutation,
} from "../../services/rtkApi/messengerApi";
import { MESSENGER_URL } from "../../config/env";
import { useWebPush } from "../../hooks/useWebPush";
import styles from "./TelegramSettings.module.css";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

function formatDeviceLabel(ua: string | null): string {
  if (!ua?.trim()) return "Browser or device (unknown user agent)";
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const m = ua.match(/OS (\d+[._]\d+)/);
    return m ? `Apple device (iOS ${m[1].replace("_", ".")})` : "Apple device (iOS)";
  }
  if (/Android/i.test(ua)) return "Android";
  if (/Edg\//.test(ua)) return "Microsoft Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return ua.length > 72 ? `${ua.slice(0, 69)}…` : ua;
}

function formatAddedAt(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "";
  }
}

/** Safari (and pure WebKit) — excludes Chrome, Edge, Firefox on iOS */
function isSafariFamily(): boolean {
  const ua = navigator.userAgent;
  if (!/safari/i.test(ua)) return false;
  if (/chrome|chromium|crios|fxios|edgios|edg\//i.test(ua)) return false;
  return true;
}

function SafariPushInstructions() {
  return (
    <div className={styles.safariInstructions}>
      <p className={styles.safariInstructionsLead}>
        On Apple devices, web push only works in an <strong>installed web app</strong>, not in a normal Safari tab. That is
        why this page shows push as unavailable.
      </p>
      <p className={styles.safariInstructionsSub}>iPhone or iPad</p>
      <ol className={styles.safariInstructionsList}>
        <li>
          Tap <strong>Share</strong> (square with arrow), then <strong>Add to Home Screen</strong>, then Add.
        </li>
        <li>
          Open n450s using <strong>that home screen icon</strong> (do not use a regular Safari tab).
        </li>
        <li>Sign in, then use Enable notifications here or on your Profile page.</li>
      </ol>
      <p className={styles.safariInstructionsSub}>Mac (Safari 17+, macOS Sonoma or newer)</p>
      <ol className={styles.safariInstructionsList}>
        <li>
          With the site open in Safari, use <strong>File → Add to Dock</strong> or <strong>Share → Add to Dock</strong>.
        </li>
        <li>
          Open the site from the <strong>Dock web app</strong>, sign in, then enable notifications.
        </li>
      </ol>
      <p className={styles.safariInstructionsFooter}>
        On Mac you can also use <strong>Chrome</strong> or <strong>Firefox</strong> for push in a normal browser window,
        without installing the web app.
      </p>
    </div>
  );
}

export interface WebPushSettingsProps {
  /** home = tighter margins for embedding on the landing page */
  placement?: "profile" | "home";
}

const WebPushSettings: React.FC<WebPushSettingsProps> = ({ placement = "profile" }) => {
  const token = useAppSelector((s) => s.auth.token);
  const showDebugHints = process.env.NODE_ENV !== "production";
  const {
    serverConfigured,
    pushServerReadiness,
    localSubscribed,
    localPushEndpoint,
    subscribing,
    unsubscribing,
    error,
    setError,
    subscribe,
    unsubscribe,
  } = useWebPush(!!token);

  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useGetPushStatusQuery(
    { endpoint: localPushEndpoint ?? undefined },
    { skip: !token || localPushEndpoint === undefined },
  );

  const [unsubscribeById] = useUnsubscribePushMutation();
  const [sendTestPush, { isLoading: testingPush }] = useSendTestPushMutation();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [testFeedback, setTestFeedback] = useState<string | null>(null);

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const onToggle = useCallback(async () => {
    setError(null);
    if (localSubscribed) {
      const ok = await unsubscribe();
      if (ok) refetchStatus();
    } else {
      const ok = await subscribe();
      if (ok) refetchStatus();
    }
  }, [localSubscribed, refetchStatus, setError, subscribe, unsubscribe]);

  const removeOtherDevice = useCallback(
    async (subscriptionId: string) => {
      setError(null);
      setRemovingId(subscriptionId);
      try {
        const res = await unsubscribeById({ subscriptionId }).unwrap();
        if (!res.success) {
          setError(res.error || "Could not remove that device.");
        } else if (!res.data?.removed) {
          setError("That registration was not found (it may have expired already).");
        } else {
          await refetchStatus();
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setRemovingId(null);
      }
    },
    [setError, unsubscribeById, refetchStatus],
  );

  const onTestPush = useCallback(async () => {
    setTestFeedback(null);
    setError(null);
    try {
      const res = await sendTestPush().unwrap();
      if (res.success) {
        setTestFeedback("Sent — check banners on this device or others you registered.");
      } else {
        setTestFeedback(res.error || "Could not send test.");
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "data" in e && (e as { data?: { error?: string } }).data?.error
          ? (e as { data: { error: string } }).data.error
          : e instanceof Error
            ? e.message
            : String(e);
      setTestFeedback(msg);
    }
  }, [sendTestPush, setError]);

  const busy = subscribing || unsubscribing || localSubscribed === null || localPushEndpoint === undefined;
  const statusBlockLoading = localPushEndpoint === undefined || statusLoading;
  const devices = statusData?.data?.devices ?? [];

  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faBell} className={styles.titleIcon} />
          Browser notifications
        </div>
        <p className={styles.description}>Sign in to enable push notifications from n450s.</p>
      </div>
    );
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    const safari = isSafariFamily();
    return (
      <div className={`${styles.container} ${placement === "home" ? styles.homeEmbed : ""}`}>
        <div className={styles.title}>
          <FontAwesomeIcon icon={faBell} className={styles.titleIcon} />
          Browser notifications
        </div>
        {safari ? (
          <>
            <p className={styles.description}>
              Push is not available in this Safari view. Follow the steps below, then come back in the{" "}
              <strong>installed app</strong>.
            </p>
            <SafariPushInstructions />
            {placement === "home" ? (
              <p className={styles.safariInlineHint}>
                Full device list and test button also live on <strong>Profile</strong> after you enable push in the web
                app.
              </p>
            ) : null}
          </>
        ) : (
          <p className={styles.description}>
            This browser does not expose web push (or it is disabled). Try a current version of{" "}
            <strong>Chrome</strong>, <strong>Firefox</strong>, or <strong>Edge</strong> on desktop or Android.
          </p>
        )}
      </div>
    );
  }

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);

  return (
    <div className={`${styles.container} ${placement === "home" ? styles.homeEmbed : ""}`}>
      <div className={styles.title}>
        <FontAwesomeIcon icon={faBell} className={styles.titleIcon} />
        Browser notifications
      </div>
      <p className={styles.description}>
        Get notified about comments, milestones, agent chat, and other updates. Works best on desktop Chrome or Android.
        Use <strong>Send test</strong> below to verify delivery on this or other registered devices.
        {isIOS ? (
          <>
            {" "}
            On iOS, add this site to your Home Screen and open n450s from that icon before enabling push.
          </>
        ) : null}
        {isSafariFamily() && !isIOS ? (
          <>
            {" "}
            On Mac Safari in a normal tab, push is often unavailable — use <strong>Add to Dock</strong> for this site, or
            Chrome/Firefox.
          </>
        ) : null}
      </p>

      {statusBlockLoading ? (
        <p className={styles.loadingText}>Loading…</p>
      ) : (
        <>
          {pushServerReadiness === "unreachable" && (
            <>
              <p className={styles.errorText}>
                Browser notifications are unavailable right now.
                {showDebugHints ? (
                  <>
                    {" "}
                    Could not load push settings from messenger ({MESSENGER_URL}). Is the service running and does{" "}
                    <code className={styles.codeInline}>REACT_APP_MESSENGER_URL</code> match?
                  </>
                ) : null}
              </p>
              {isSafariFamily() ? (
                <p className={styles.safariInlineHint}>
                  If you are in Safari but already added the site to the Home Screen or Dock, open n450s from{" "}
                  <strong>that icon</strong> — the in-tab and installed app use different storage.
                </p>
              ) : null}
            </>
          )}
          {pushServerReadiness === "not_configured" && (
            <>
              <p className={styles.errorText}>
                Browser notifications are unavailable right now.
                {showDebugHints ? (
                  <>
                    {" "}
                    Messenger is reporting push as not configured. In <code className={styles.codeInline}>n450s_messenger</code>{" "}
                    set <code className={styles.codeInline}>WEB_PUSH_VAPID_PUBLIC_KEY</code> and{" "}
                    <code className={styles.codeInline}>WEB_PUSH_VAPID_PRIVATE_KEY</code> (run{" "}
                    <code className={styles.codeInline}>npx web-push generate-vapid-keys</code>), then restart messenger.
                  </>
                ) : null}
              </p>
              {isSafariFamily() ? (
                <p className={styles.safariInlineHint}>
                  Safari only shows push controls in a supported browser context; server misconfiguration is separate
                  from Apple’s web-app requirement.
                </p>
              ) : null}
            </>
          )}
          {error && <p className={styles.errorText}>{error}</p>}
          <div className={styles.statusRow}>
            <span className={styles.infoLabel}>This device:</span>
            <span
              className={
                localSubscribed === null
                  ? styles.statusDisconnected
                  : localSubscribed
                    ? styles.statusConnected
                    : styles.statusDisconnected
              }
            >
              {localSubscribed === null ? "Checking…" : localSubscribed ? "On" : "Off"}
            </span>
          </div>
          {!localSubscribed && devices.length > 0 ? (
            <p className={styles.mutedHint}>
              This browser is not registered yet. You can add it with <strong>Enable notifications</strong> while other
              devices stay registered.
            </p>
          ) : null}

          {devices.length > 0 ? (
            <div className={styles.deviceList}>
              <div className={styles.deviceListTitle}>Registered for push ({devices.length})</div>
              {devices.map((d) => (
                <div key={d.id} className={styles.deviceRow}>
                  <div className={styles.deviceMeta}>
                    <div>{formatDeviceLabel(d.userAgent)}</div>
                    {d.addedAt ? (
                      <div style={{ fontSize: "0.8rem", color: "#888" }}>Added {formatAddedAt(d.addedAt)}</div>
                    ) : null}
                  </div>
                  {d.isThisDevice ? (
                    <span className={styles.thisDevicePill}>This device</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.removeDeviceBtn}
                      disabled={!!removingId || unsubscribing}
                      onClick={() => void removeOtherDevice(d.id)}
                    >
                      {removingId === d.id ? "Removing…" : "Remove"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : serverConfigured && pushServerReadiness === "configured" ? (
            <p className={styles.mutedHint}>No devices registered yet — enable notifications on each browser or phone you use.</p>
          ) : null}

          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.connectButton}
              style={{ backgroundColor: "#1a1a1a" }}
              disabled={!serverConfigured || busy || localSubscribed === null}
              title={
                !serverConfigured
                  ? pushServerReadiness === "unreachable"
                    ? "Fix messenger URL or start the service"
                    : "Add VAPID keys to messenger .env and restart"
                  : undefined
              }
              onClick={() => void onToggle()}
            >
              <FontAwesomeIcon icon={faBell} />
              {localSubscribed === null
                ? "…"
                : localSubscribed
                  ? "Turn off notifications"
                  : "Enable notifications"}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={!serverConfigured || testingPush || pushServerReadiness !== "configured"}
              onClick={() => void onTestPush()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              {testingPush ? "Sending…" : "Send test notification"}
            </button>
            {testFeedback ? (
              <p
                className={`${styles.testPushFeedback} ${
                  testFeedback.startsWith("Sent") ? styles.testPushFeedbackOk : styles.testPushFeedbackErr
                }`}
              >
                {testFeedback}
              </p>
            ) : null}
            {!serverConfigured && showDebugHints && pushServerReadiness !== "loading" && pushServerReadiness !== "skipped" ? (
              <p className={styles.description} style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                Buttons stay disabled until messenger reports push is configured (see above).
              </p>
            ) : null}
          </div>
        </>
      )}

      {deferredPrompt && (
        <button
          type="button"
          className={styles.settingsLink}
          style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
          onClick={() => {
            void deferredPrompt.prompt().then(() => setDeferredPrompt(null));
          }}
        >
          <FontAwesomeIcon icon={faMobileScreen} /> Install app (optional)
        </button>
      )}
    </div>
  );
};

export default WebPushSettings;
